import { PUBLIC_DO_ASSETS_URL } from '$env/static/public';

import { error } from '@sveltejs/kit';

import { MAX_LIMIT } from '@do/models';

import { ArtAssetCardApi } from '@entities/art-asset';
import { SelectionApi } from '@entities/selection';
import { slugLibs } from '@entities/slug';

import { artworkPageLibs } from '@pages/artwork';

import type { PageLoad } from './$types';

export const ssr = true;

export const load: PageLoad = async ({ data, fetch, params }) => {
  if (isNaN(Number(params.id)) || !params.slug) {
    throw Error('Invalid params id or slug');
  }

  const artAssetCardApi = new ArtAssetCardApi(fetch);
  const selectionApi = new SelectionApi(fetch);

  const selectionsQueryParams = new URLSearchParams({ art_assets: params.id, limit: String(MAX_LIMIT) });

  const [artwork, similarArtworks, selections] = await Promise.all([
    artAssetCardApi.readOne(params.id),
    artAssetCardApi.readManySimilar({ id: params.id, institutionId: data.configurationsId }),
    selectionApi.readMany(selectionsQueryParams),
  ]);

  if (!artwork.id) {
    throw error(404, { message: 'Artwork not found' });
  }

  slugLibs.handleSlugMismatch({
    currentSlug: params.slug,
    entitySlug: artwork.slug,
    redirectPath: `/artwork-${artwork.id}-${artwork.slug}/`,
  });

  const defaultPreset = artwork.main_photo?.asset_transformations?.find(
    (preset) => preset.preset_key === 'default-1500w-webp' || preset.preset_key === 'default-500w-webp',
  );

  const breadcrumbs = [
    {
      name: 'Home',
      slug: '/',
      canonical: false,
    },
    {
      name: 'Artworks',
      slug: '/artworks/',
      canonical: false,
    },
    {
      name: artwork.name_english,
      slug: `/artwork-${artwork.id}-${artwork.slug}/`,
      canonical: true,
    },
  ];

  const { purchase, report, configurationsType, associatedEntityName } = data;
  const artistName = artwork.artists[0].name;

  const seoData = new artworkPageLibs.Seo(
    configurationsType,
    {
      entityName: associatedEntityName,
      artistName,
      artworkName: artwork.name_english,
    },
    {
      metaImage: {
        alt: artwork.name_english,
        caption: artwork.name_english,
        url: `${PUBLIC_DO_ASSETS_URL}/${defaultPreset?.transformation_name}`,
        width: artwork.main_photo?.width ?? 0,
        height: artwork.main_photo?.height ?? 0,
      },
      breadcrumbs,
      robots: true,
    },
  );

  const seo = seoData.generateSeoConfig();

  return {
    artwork,
    similarArtworks,
    selections,
    forms: {
      purchase,
      report,
    },
    seo,
  };
};
